import {
  CustomizeUIStateType,
  CustomizeUIContextType,
  CustomizeUIActionSetType,
  CustomizeUIActionType,
  CouponEditorConfigType,
  PaymentMethodConfigType,
  ThemeColorPaletteType,
  BannerConfigType,
  CheckoutPageType,
  CTAButtonPageType,
  CouponVisibilityConfigType,
  DeliveryInfoConfigType,
  TrustBadgesConfigType,
  OrderSummaryConfigType,
  EtdProviderType,
  CustomizeUIPageContextType,
  MarketingFlagConfigType,
  DialogDisplayConfigType
} from '../types/customizeUI'
import { createContext, useContextSelector } from 'use-context-selector'
import {
  getDefaultCustomizeUIState,
  thankYouPageDefaultState,
  thankYouPageLegacyDefaultState,
  DELHIVERY_CONFIRMATION_DIALOG_KEY
} from 'src/assets/CustomizeUIConfig'
import { FC, ReactNode, useCallback, useMemo, useReducer, useState, useEffect } from 'react'
import { useLoader } from './LoaderContext'
import { useLocale } from '../hooks/useLocal'
import { useTemporary } from './TemporaryContext'
import { useMerchant } from './MerchantContext'
import { useCheckoutUI } from './CheckoutUIContext'
import produce from 'immer'
import { SwitchableTabType } from '../types/random'
import useSWR from 'swr'
import { fetcher, getRequest, staticOptions } from '../core/api-client'
import { getDiscounts, setMerchantAttributes, setUIAttributes } from '../core/api-methods'
import { discountsAdapter } from '../utils/adapters'
import { errorToast, successToast } from '../utils/toasters'
import {
  compareCustomizeUIMerchantAttributes,
  getCheckoutUIPayload,
  handleUpdatePaymentMethods,
  parseBannerConfig,
  parseCTAButtonConfig,
  parseDiscountVisibilityConfig,
  parsePaymentModesResponse,
  parseMarketingFlag,
  parseDialogDisplayConfig
} from '../utils/customizeUIHelpers'
import { compareStringConvertValue, createDeepCopy } from '../utils/helpers'
import { handleSaveDiscountsWeight } from '../utils/customizeUIHelpers'
import useConfirmOnRouteChanges from '../utils/custom-hooks/useConfirmOnRouteChanges'

import { useActivityLogs, EntityName } from '../hooks/useActivityLogs'
import { apiURI, DiscountCallArgCouponType } from '../utils/constants'
import {
  ElementStructure,
  FeatureState,
  FeatureTypes
} from 'src/components/ui-customization/sub-components/thankyou-page/types'
import { AddressConfigType, MerchantAttributesType } from '../types/merchantAttributes'
import { SFContextType } from '../utils/attributesParsers'
import { createUIAttributePayload } from '../utils/attributesParsers'
import { Feature } from '../types/merchantPricing'
import { useRouter } from 'next/router'
import { ExperimentUIConfigType } from '../types/experiments'

// Discount Issuer ID
const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

const CustomizeUIContext = createContext<CustomizeUIContextType | undefined>(undefined)

const reducer = produce((draft: CustomizeUIStateType, action: CustomizeUIActionType) => {
  switch (action.type) {
    case CustomizeUIActionSetType.SET_COMPLETE_CONFIG:
      return action.payload
    case CustomizeUIActionSetType.SET_TAB_LIST_DATA:
      draft.tabList = action.payload
      break
    case CustomizeUIActionSetType.SET_COUPON_EDITOR_DATA:
      draft.couponEditorConfig = action.payload
      break
    case CustomizeUIActionSetType.SET_PAYMENTS_METHODS_DATA:
      draft.paymentMethodsConfig = action.payload
      break
    case CustomizeUIActionSetType.SET_BRAND_LOGO_URL_DATA:
      draft.brandLogoURL = action.payload
      break
    case CustomizeUIActionSetType.SET_COLOR_PALETTE_DATA:
      draft.colorPalette = action.payload
      break
    case CustomizeUIActionSetType.SET_BANNER_CONFIG_DATA:
      switch (action.payload.type) {
        case 'AUTH':
          draft.bannerConfigs.loginBanner = action.payload.bannerConfig
          break
        case 'ORDER_SUMMARY':
          draft.bannerConfigs.addressBanner = action.payload.bannerConfig
          break
        case 'PAYMENTS':
          draft.bannerConfigs.paymentBanner = action.payload.bannerConfig
          break
      }
      break
    case CustomizeUIActionSetType.SET_BUTTON_CTA_TEXT_DATA:
      switch (action.payload.type) {
        case 'AUTH':
          draft.buttonCTATexts.loginButtonCTAText = action.payload.CTAText
          break
        case 'ORDER_SUMMARY':
          draft.buttonCTATexts.addressButtonCTAText = action.payload.CTAText
          break
      }
      break
    case CustomizeUIActionSetType.SET_COUPON_VISIBILITY_CONFIG_DATA:
      switch (action.payload.type) {
        case 'AUTH':
          draft.couponVisibilityConfig.loginCouponVisibility = action.payload.couponVisibilityConfig
          break
        case 'ORDER_SUMMARY':
          draft.couponVisibilityConfig.addressCouponVisibility = action.payload.couponVisibilityConfig
          break
        case 'PAYMENTS':
          draft.couponVisibilityConfig.paymentsCouponVisibility = action.payload.couponVisibilityConfig
          break
      }
      break
    case CustomizeUIActionSetType.SET_DELIVERY_INFO_CONFIG_DATA:
      draft.deliveryInfoConfig = action.payload
      break
    case CustomizeUIActionSetType.SET_TRUST_BADGES_CONFIG_DATA:
      draft.trustBadgesConfig = action.payload
      break
    case CustomizeUIActionSetType.SET_ORDER_SUMMARY_CONFIG: {
      const wasOnePageCheckoutEnable = draft.orderSummaryConfig.isOnePageCheckoutEnable
      draft.orderSummaryConfig = action.payload
      if (wasOnePageCheckoutEnable && !action.payload.isOnePageCheckoutEnable) {
        const delhiveryDialog = draft.dialogDisplayConfig.find(
          dialog => dialog.key === DELHIVERY_CONFIRMATION_DIALOG_KEY
        )
        if (delhiveryDialog) delhiveryDialog.enabled = false
      }
      break
    }
    case CustomizeUIActionSetType.SET_ETD_PROVIDERS_DATA:
      draft.etdProviders = action.payload
      break
    case CustomizeUIActionSetType.SET_THANK_YOU_PAGE_FEATURE_STATE:
      draft.thankYouPageConfig.mainFeatureState = action.payload
      break
    case CustomizeUIActionSetType.SET_ADDRESS_CONFIG_DATA:
      draft.addressConfig = action.payload
      break
    case CustomizeUIActionSetType.SET_MARKETING_FLAG_CONFIG_DATA:
      draft.marketingFlagConfig = action.payload
      break
    case CustomizeUIActionSetType.SET_DIALOG_DISPLAY_CONFIG_DATA:
      draft.dialogDisplayConfig = action.payload
      break
    case CustomizeUIActionSetType.SET_BANK_OFFERS_CONFIG_DATA:
      draft.showBankOffers = action.payload
      break
    default:
      break
  }
})

export const CustomizeUIProvider: FC<{
  pageContext?: CustomizeUIPageContextType
  children: ReactNode
  initialState?: ExperimentUIConfigType
}> = ({ pageContext = CustomizeUIPageContextType.CUSTOMIZE_UI, children, initialState }) => {
  const { sendActivityLog } = useActivityLogs()
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()
  const { temporary, setTemporaryData } = useTemporary()
  const {
    getters: { merchant }
  } = useMerchant()
  const {
    getters: { checkoutUI, isOnePageCheckoutEnable },
    actions: { updateCheckoutUI }
  } = useCheckoutUI()
  const router = useRouter()

  const [initialCustomizeUIState, setInitialCustomizeUIState] = useState<CustomizeUIStateType>(
    getDefaultCustomizeUIState(pageContext)
  )
  const [reducerState, dispatch] = useReducer(
    reducer,
    initialCustomizeUIState || getDefaultCustomizeUIState(pageContext)
  )
  const activeTab = reducerState.tabList.find(tab => tab.isActive)
  const [isChanged, setIsChanged] = useState<boolean>(false)

  const currentRoute = router.pathname

  const { data: paymentModesResponse, isValidating: fetchingPaymentModes } = useSWR(
    currentRoute === '/style/payment' ? `/fms/v1/merchant-payment-modes` : '',
    fetcher,
    staticOptions
  )

  const {
    data: thankYouPageResponse,
    isValidating: fetchingThankYouPage,
    mutate: revalidateThankYouPage
  } = useSWR<
    {
      ui_attributes: FeatureState
    },
    any,
    [string, string | undefined] | null
  >(
    [`/md/merchants/ui-attributes?channel=web&sfContext=THANK_YOU_PAGE`, apiURI.FLO_KRATOS_CHECKOUT],
    async ([url, baseURI]) => {
      const response = await fetcher(url, baseURI)
      return response as { ui_attributes: FeatureState }
    },
    staticOptions
  )

  useEffect(() => {
    if (temporary) {
      dispatch({
        type: CustomizeUIActionSetType.SET_COMPLETE_CONFIG,
        payload: temporary
      })
      setTemporaryData(undefined)
    }
  }, [temporary])

  useConfirmOnRouteChanges(
    isChanged && pageContext !== CustomizeUIPageContextType.EXPERIMENTS,
    () => {
      return new Promise((resolve: any, reject) => {
        if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
          setTemporaryData(reducerState)
          resolve(true)
        } else {
          reject(false)
        }
      })
    },
    reducerState
  )

  useEffect(() => {
    setIsChanged(!compareStringConvertValue(reducerState, initialCustomizeUIState))
  }, [reducerState, initialCustomizeUIState])

  useEffect(() => {
    if (currentRoute !== '/style/payment') return
    if (!fetchingPaymentModes) {
      return
    }
  }, [fetchingPaymentModes, currentRoute])

  useEffect(() => {
    if (!checkoutUI) return

    if (pageContext === CustomizeUIPageContextType.EXPERIMENTS) return

    const fetchProviders = async () => {
      try {
        const response: any = await getRequest('/utility/external-etd-config-providers', apiURI.SHIPPING_ENGINE)
        const etdProviders = response?.data ?? []
        setInitialCustomizeUIState(
          produce((draft: CustomizeUIStateType) => {
            draft.etdProviders = etdProviders
          })
        )
        setETDProvidersData(etdProviders)
      } catch (error) {
        errorToast(t('error_fetching_etd_providers'))
      }
    }

    fetchProviders()
  }, [merchant?.attributes, checkoutUI])

  const filterThankyouPageState = (
    thankYouPageData: any,
    merchant: MerchantAttributesType,
    isExistingMerchant: boolean
  ) => {
    const thankYouPageAttributes = thankYouPageData?.thank_you_page ?? thankYouPageData ?? {}

    const baseDefaultState = isExistingMerchant ? thankYouPageLegacyDefaultState : thankYouPageDefaultState
    const state: FeatureState = { ...baseDefaultState }

    Object.keys(thankYouPageAttributes).forEach(key => {
      const featureKey = key as FeatureTypes
      const featureData = thankYouPageAttributes[featureKey]

      if (featureData && typeof featureData === 'object' && !Array.isArray(featureData)) {
        const baseData = state[featureKey]
        state[featureKey] = {
          ...baseData,
          ...featureData
        } as FeatureState[FeatureTypes]
      }
    })

    const isC2pEnabled = merchant?.attributes?.addressConfig?.c2pConfig?.isEnabled
    const activeBadges = checkoutUI?.trustBadgeConfig?.badges?.filter(badge => badge.isActive)

    if (!isC2pEnabled) {
      delete state.COD_TO_PREPAID
    } else if (!thankYouPageAttributes.COD_TO_PREPAID) {
      state.COD_TO_PREPAID = {
        ...thankYouPageDefaultState.COD_TO_PREPAID,
        disabled: true
      }
    }

    if (activeBadges?.length === 0) {
      delete state.TRUST_BADGE
    } else if (!thankYouPageAttributes.TRUST_BADGE) {
      state.TRUST_BADGE = {
        ...thankYouPageDefaultState.TRUST_BADGE,
        disabled: true
      }
    }

    return state
  }

  // Setting Merchant Attributes in Context
  useEffect(() => {
    if (!merchant?.attributes || !checkoutUI) return
    setInitialCustomizeUIState(
      produce((draft: CustomizeUIStateType) => {
        draft.thankYouPageConfig.mainFeatureState = filterThankyouPageState(
          thankYouPageResponse?.ui_attributes ?? thankYouPageDefaultState,
          merchant,
          Boolean(thankYouPageResponse?.ui_attributes)
        )
        draft.addressConfig = merchant?.attributes?.addressConfig ?? {}
        if (initialState && initialState.brandLogoURL) {
          draft.brandLogoURL = initialState.brandLogoURL
        } else {
          draft.brandLogoURL = checkoutUI?.logoUrl ?? ''
        }
        if (initialState && initialState.bannerConfigs) {
          draft.bannerConfigs = initialState.bannerConfigs
        } else {
          draft.bannerConfigs.loginBanner = parseBannerConfig(checkoutUI?.bannerConfig, 'AUTH')
          draft.bannerConfigs.addressBanner = parseBannerConfig(checkoutUI?.bannerConfig, 'ORDER_SUMMARY')
          draft.bannerConfigs.paymentBanner = parseBannerConfig(checkoutUI?.bannerConfig, 'PAYMENTS')
        }
        draft.buttonCTATexts.loginButtonCTAText = parseCTAButtonConfig(checkoutUI?.primaryCTAConfig, 'AUTH')
        draft.buttonCTATexts.addressButtonCTAText = parseCTAButtonConfig(checkoutUI?.primaryCTAConfig, 'ORDER_SUMMARY')
        draft.colorPalette.primaryColor = checkoutUI?.colorPalette?.primaryColor ?? ''
        draft.colorPalette.primaryTextColor = checkoutUI?.colorPalette?.primaryTextColor ?? ''
        draft.couponVisibilityConfig.loginCouponVisibility = parseDiscountVisibilityConfig(
          checkoutUI?.couponVisibilityConfig,
          'AUTH'
        )
        draft.couponVisibilityConfig.addressCouponVisibility = parseDiscountVisibilityConfig(
          checkoutUI?.couponVisibilityConfig,
          'ORDER_SUMMARY'
        )
        draft.couponVisibilityConfig.paymentsCouponVisibility = parseDiscountVisibilityConfig(
          checkoutUI?.couponVisibilityConfig,
          'PAYMENTS'
        )
        draft.deliveryInfoConfig.isVisible = checkoutUI?.delivery_config?.enabled ?? false
        draft.deliveryInfoConfig.deliveryInfoText = checkoutUI?.delivery_config?.delivery_text ?? ''
        draft.deliveryInfoConfig.isETDEnabled = checkoutUI?.delivery_config?.is_etd_enabled
        draft.trustBadgesConfig.showBadges = checkoutUI?.trustBadgeConfig?.showBadges
        draft.trustBadgesConfig.badges = checkoutUI?.trustBadgeConfig?.badges
        draft.orderSummaryConfig.isOrderSummaryOpen = checkoutUI?.isOrderSummaryOpen
        draft.orderSummaryConfig.isQuantityEditable = checkoutUI?.isQuantityEditable
        draft.orderSummaryConfig.isPriceStrikeThroughEnabled = Boolean(
          checkoutUI?.layout?.metadata?.show_original_price
        )
        draft.orderSummaryConfig.showOrderSummaryAs = checkoutUI?.showOrderSummaryAs
        draft.orderSummaryConfig.isOnePageCheckoutEnable = isOnePageCheckoutEnable
        draft.marketingFlagConfig = parseMarketingFlag(checkoutUI)
        draft.dialogDisplayConfig = parseDialogDisplayConfig(checkoutUI?.dialog_display_config)
        draft.showBankOffers = checkoutUI?.showBankOffers ?? false
      })
    )
    if (Boolean(temporary)) return
    setAddressConfigData(merchant?.attributes?.addressConfig ?? {})
    setThankYouPageFeatureState(
      filterThankyouPageState(
        thankYouPageResponse?.ui_attributes ?? thankYouPageDefaultState,
        merchant,
        Boolean(thankYouPageResponse?.ui_attributes)
      )
    )
    if (initialState && initialState.brandLogoURL) {
      setBrandLogoURLData(initialState.brandLogoURL)
    } else {
      setBrandLogoURLData(checkoutUI?.logoUrl ?? '')
    }
    if (initialState && initialState.bannerConfigs) {
      setBannerConfigData(initialState.bannerConfigs.loginBanner, 'AUTH')
      setBannerConfigData(initialState.bannerConfigs.addressBanner, 'ORDER_SUMMARY')
      setBannerConfigData(initialState.bannerConfigs.paymentBanner, 'PAYMENTS')
    } else {
      setBannerConfigData(parseBannerConfig(checkoutUI?.bannerConfig, 'AUTH'), 'AUTH')
      setBannerConfigData(parseBannerConfig(checkoutUI?.bannerConfig, 'ORDER_SUMMARY'), 'ORDER_SUMMARY')
      setBannerConfigData(parseBannerConfig(checkoutUI?.bannerConfig, 'PAYMENTS'), 'PAYMENTS')
    }
    setButtonCTATextData(parseCTAButtonConfig(checkoutUI?.primaryCTAConfig, 'AUTH'), 'AUTH')
    setButtonCTATextData(parseCTAButtonConfig(checkoutUI?.primaryCTAConfig, 'ORDER_SUMMARY'), 'ORDER_SUMMARY')
    setColorPaletteData({
      primaryColor: checkoutUI?.colorPalette?.primaryColor ?? '',
      primaryTextColor: checkoutUI?.colorPalette?.primaryTextColor ?? ''
    })
    setCouponVisibilityConfigData(parseDiscountVisibilityConfig(checkoutUI?.couponVisibilityConfig, 'AUTH'), 'AUTH')
    setCouponVisibilityConfigData(
      parseDiscountVisibilityConfig(checkoutUI?.couponVisibilityConfig, 'ORDER_SUMMARY'),
      'ORDER_SUMMARY'
    )
    setCouponVisibilityConfigData(
      parseDiscountVisibilityConfig(checkoutUI?.couponVisibilityConfig, 'PAYMENTS'),
      'PAYMENTS'
    )
    setDeliveryInfoConfigData({
      isVisible: checkoutUI?.delivery_config?.enabled ?? false,
      deliveryInfoText: checkoutUI?.delivery_config?.delivery_text ?? '',
      isETDEnabled: checkoutUI?.delivery_config?.is_etd_enabled
    })
    setTrustBadgesConfigData({
      showBadges: checkoutUI?.trustBadgeConfig?.showBadges,
      badges: checkoutUI?.trustBadgeConfig?.badges
    })
    setOrderSummaryConfigData({
      isOrderSummaryOpen: checkoutUI?.isOrderSummaryOpen,
      isPriceStrikeThroughEnabled: Boolean(checkoutUI?.layout?.metadata?.show_original_price),
      isQuantityEditable: checkoutUI?.isQuantityEditable,
      isOnePageCheckoutEnable: isOnePageCheckoutEnable,
      showOrderSummaryAs: checkoutUI?.showOrderSummaryAs
    })
    setMarketingFlagConfigData(parseMarketingFlag(checkoutUI))
    setDialogDisplayConfigData(parseDialogDisplayConfig(checkoutUI?.dialog_display_config))
    setBankOffersConfigData(checkoutUI?.showBankOffers ?? false)
  }, [merchant?.attributes, thankYouPageResponse, checkoutUI, isOnePageCheckoutEnable])

  // Setting Payments Response
  useEffect(() => {
    if (!Boolean(paymentModesResponse) && fetchingPaymentModes) return
    const paymentMethods = parsePaymentModesResponse(paymentModesResponse)
    setInitialCustomizeUIState(
      produce((draft: CustomizeUIStateType) => {
        draft.paymentMethodsConfig = paymentMethods
      })
    )
    if (Boolean(temporary)) return
    setPaymentsMethodsData(paymentMethods)
  }, [paymentModesResponse, fetchingPaymentModes])

  useEffect(() => {
    if (typeof merchant === 'undefined' || (currentRoute !== '/style/coupons' && activeTab?.name !== 'COUPONS')) return
    reFetchCouponEditorData()
  }, [merchant, currentRoute, activeTab])

  const reFetchCouponEditorData = useCallback(async () => {
    try {
      const customizableDiscountResponse: any = await getDiscounts(
        issuerId,
        merchant?.accountId,
        10,
        1,
        'discounts',
        '',
        '',
        true,
        `PREPAID,${DiscountCallArgCouponType}`
      )
      if (customizableDiscountResponse?.error) {
        errorToast(customizableDiscountResponse?.error)
        console.error('x-trace-id:', customizableDiscountResponse.headers?.['x-trace-id'])
        return
      }
      const rows = customizableDiscountResponse?.data?.data?.map((row: any, index: number) => {
        return discountsAdapter(row, index + 1)
      })
      setInitialCustomizeUIState(
        produce((draft: CustomizeUIStateType) => {
          draft.couponEditorConfig = rows
        })
      )
      if (Boolean(temporary)) return
      setCouponEditorData(rows)
    } catch (error) {
      console.error(error)
    } finally {
    }
  }, [merchant])

  const setCompleteState = useCallback((state: CustomizeUIStateType) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_COMPLETE_CONFIG,
      payload: state
    })
  }, [])

  const setTabListData = useCallback(
    (tablist: SwitchableTabType[]) => {
      if (
        isChanged &&
        pageContext !== CustomizeUIPageContextType.EXPERIMENTS &&
        confirm('You have unsaved changes. Do you want to stay on the page?')
      ) {
        return
      }
      if (pageContext !== CustomizeUIPageContextType.EXPERIMENTS) {
        setCompleteState(initialCustomizeUIState)
      }
      setInitialCustomizeUIState(
        produce((draft: CustomizeUIStateType) => {
          draft.tabList = tablist
        })
      )
      dispatch({
        type: CustomizeUIActionSetType.SET_TAB_LIST_DATA,
        payload: tablist
      })
    },
    [isChanged, initialCustomizeUIState, setCompleteState]
  )

  const setCouponEditorData = useCallback((couponList: CouponEditorConfigType[]) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_COUPON_EDITOR_DATA,
      payload: couponList
    })
  }, [])

  const setPaymentsMethodsData = useCallback((paymentModes: PaymentMethodConfigType[]) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_PAYMENTS_METHODS_DATA,
      payload: paymentModes
    })
  }, [])

  const setBrandLogoURLData = useCallback((logoURL: string) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_BRAND_LOGO_URL_DATA,
      payload: logoURL
    })
  }, [])

  const setColorPaletteData = useCallback((colorPalette: ThemeColorPaletteType) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_COLOR_PALETTE_DATA,
      payload: colorPalette
    })
  }, [])

  const setBannerConfigData = useCallback((bannerConfig: BannerConfigType, type: CheckoutPageType) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_BANNER_CONFIG_DATA,
      payload: {
        bannerConfig: bannerConfig,
        type: type
      }
    })
  }, [])

  const setButtonCTATextData = useCallback((CTAText: string, type: CTAButtonPageType) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_BUTTON_CTA_TEXT_DATA,
      payload: {
        CTAText: CTAText,
        type: type
      }
    })
  }, [])

  const setCouponVisibilityConfigData = useCallback(
    (couponVisibilityConfig: CouponVisibilityConfigType, type: CheckoutPageType) => {
      dispatch({
        type: CustomizeUIActionSetType.SET_COUPON_VISIBILITY_CONFIG_DATA,
        payload: {
          couponVisibilityConfig: couponVisibilityConfig,
          type: type
        }
      })
    },
    []
  )

  const setDeliveryInfoConfigData = useCallback((deliveryInfoConfig: DeliveryInfoConfigType) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_DELIVERY_INFO_CONFIG_DATA,
      payload: deliveryInfoConfig
    })
  }, [])

  const setTrustBadgesConfigData = useCallback((trustBadgesConfig: TrustBadgesConfigType) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_TRUST_BADGES_CONFIG_DATA,
      payload: trustBadgesConfig
    })
  }, [])

  const setOrderSummaryConfigData = useCallback((orderSummaryConfig: OrderSummaryConfigType) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_ORDER_SUMMARY_CONFIG,
      payload: orderSummaryConfig
    })
  }, [])

  const setETDProvidersData = useCallback((etdProviders: EtdProviderType[]) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_ETD_PROVIDERS_DATA,
      payload: etdProviders
    })
  }, [])

  const setThankYouPageFeatureState = useCallback((featureState: FeatureState) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_THANK_YOU_PAGE_FEATURE_STATE,
      payload: featureState
    })
  }, [])

  const setAddressConfigData = useCallback((addressConfig: AddressConfigType) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_ADDRESS_CONFIG_DATA,
      payload: addressConfig
    })
  }, [])

  const setMarketingFlagConfigData = useCallback((marketingFlagConfig: MarketingFlagConfigType) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_MARKETING_FLAG_CONFIG_DATA,
      payload: marketingFlagConfig
    })
  }, [])

  // Saving Checkout UI configuration (all keys now live on the CHECKOUT ui-attributes)
  const saveMerchantData = async () => {
    const uiPayload = getCheckoutUIPayload(reducerState)
    try {
      const response: any = await updateCheckoutUI(uiPayload, { refreshEssentials: true })
      if (response?.error) {
        errorToast(response?.error)
      } else {
        successToast(t('alert_customization_saved'))
      }
    } catch (e) {
      errorToast(t('alert_customization_failed'))
      console.error(e)
    }
  }

  const setDialogDisplayConfigData = useCallback((dialogs: DialogDisplayConfigType[]) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_DIALOG_DISPLAY_CONFIG_DATA,
      payload: dialogs
    })
  }, [])

  const setBankOffersConfigData = useCallback((showBankOffers: boolean) => {
    dispatch({
      type: CustomizeUIActionSetType.SET_BANK_OFFERS_CONFIG_DATA,
      payload: showBankOffers
    })
  }, [])

  // Save Action
  const saveChanges = useCallback(async () => {
    const promises: Promise<unknown | void>[] = []

    // The CHECKOUT ui-attributes save below already persists logo_url and the CHECKOUT
    // enable_marketing_flag (both live in getCheckoutUIPayload), and refreshEssentials keeps
    // the branding block in sync. CART/SSO now own their own marketing-flag toggle on their
    // respective customization pages, so we no longer mirror those keys from here.
    if (compareCustomizeUIMerchantAttributes(initialCustomizeUIState, reducerState)) {
      promises.push(saveMerchantData())
    }
    // items_editable still lives on merchant attributes (server-side checkout config reads it),
    // so mirror the order-summary "items editable" toggle there alongside the CHECKOUT
    // layout.items_mutable ui-attribute written in getCheckoutUIPayload.
    if (
      initialCustomizeUIState?.orderSummaryConfig?.isQuantityEditable !==
      reducerState?.orderSummaryConfig?.isQuantityEditable
    ) {
      promises.push(
        setMerchantAttributes(
          { attributes: { items_editable: reducerState.orderSummaryConfig.isQuantityEditable } },
          merchant?.accountId ?? ''
        )
      )
    }
    if (!compareStringConvertValue(initialCustomizeUIState?.couponEditorConfig, reducerState?.couponEditorConfig)) {
      promises.push(
        handleSaveDiscountsWeight(reducerState.couponEditorConfig, merchant?.accountId ?? '', reFetchCouponEditorData)
      )
    }
    if (!compareStringConvertValue(initialCustomizeUIState?.paymentMethodsConfig, reducerState?.paymentMethodsConfig)) {
      promises.push(handleUpdatePaymentMethods(reducerState?.paymentMethodsConfig))
    }
    if (
      !compareStringConvertValue(
        initialCustomizeUIState?.thankYouPageConfig?.mainFeatureState,
        reducerState?.thankYouPageConfig?.mainFeatureState
      )
    ) {
      const payload = createUIAttributePayload(
        SFContextType.THANK_YOU_PAGE,
        reducerState?.thankYouPageConfig?.mainFeatureState
      )
      promises.push(setUIAttributes(payload).then(() => revalidateThankYouPage()))
    }
    try {
      setLoaderProperties({ isLoaderVisible: true, message: t('saving') })
      await Promise.allSettled(promises)
      const activityLogPayload = {
        entityName: EntityName.ORDER_CONFIRMATION,
        actionName: 'edited configuration'
      }
      sendActivityLog(activityLogPayload)
      successToast(t('changes_saved'))
    } catch (e) {
      console.error(e)
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }, [reducerState, initialCustomizeUIState, merchant?.accountId, reFetchCouponEditorData, revalidateThankYouPage])

  // Discard Action
  const discardChanges = useCallback(() => {
    setCompleteState(initialCustomizeUIState)
  }, [initialCustomizeUIState])

  const value: CustomizeUIContextType = useMemo(
    () => ({
      isUpdated: isChanged,
      state: reducerState,
      actions: {
        setTabListData,
        setCouponEditorData,
        setPaymentsMethodsData,
        setBrandLogoURLData,
        setColorPaletteData,
        setBannerConfigData,
        setButtonCTATextData,
        setCouponVisibilityConfigData,
        setDeliveryInfoConfigData,
        setTrustBadgesConfigData,
        setOrderSummaryConfigData,
        reFetchCouponEditorData,
        saveChanges,
        discardChanges,
        setETDProvidersData,
        setThankYouPageFeatureState,
        setAddressConfigData,
        setMarketingFlagConfigData,
        setDialogDisplayConfigData,
        setBankOffersConfigData
      }
    }),
    [
      isChanged,
      reducerState,
      setTabListData,
      setCouponEditorData,
      setPaymentsMethodsData,
      setBrandLogoURLData,
      setColorPaletteData,
      setBannerConfigData,
      setButtonCTATextData,
      setCouponVisibilityConfigData,
      setDeliveryInfoConfigData,
      setTrustBadgesConfigData,
      setOrderSummaryConfigData,
      reFetchCouponEditorData,
      saveChanges,
      discardChanges,
      setETDProvidersData,
      setThankYouPageFeatureState,
      setAddressConfigData,
      setMarketingFlagConfigData,
      setDialogDisplayConfigData,
      setBankOffersConfigData
    ]
  )
  return <CustomizeUIContext.Provider value={value}>{children}</CustomizeUIContext.Provider>
}

export function useCustomizeUIContext<Selected>(selector: (value: CustomizeUIContextType) => Selected) {
  const { t } = useLocale()
  return useContextSelector(CustomizeUIContext, context => {
    if (context === undefined) {
      throw new Error(t('error_customize_ui_context_not_found'))
    }
    return selector(context)
  })
}
